import { useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
	FunctionaryList,
	PagingConfig,
	ApplicationsFilter,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { FunctionariesListState } from './FunctionariesList.store';
import { useSnapshot } from 'valtio';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useAuth } from '../../../hooks/useAuth';
import {
	bulkUploadFunctionaries,
	downloadTemplateForBulkUploadFunctionaries,
	getFunctionaryList,
} from '../../../service/VolunteerApplicationService';
import { useBatchId } from '../../../hooks/useBatchId';
import {
	ApplicationStatus,
	DEFAULT_PAGE_SIZE,
} from '../../../shared/Constants';
import { NavigateOptions } from 'react-router-dom';
import {
	getAndDownloadFile,
	getSignedUrl,
	uploadFilesToCloud,
} from '../../../service/ExamModuleService';
import { downloadFile } from '../../../shared/Utils';
import { SignedUrlDetail } from '../../../shared/ExamModuleTypes';
import { UploadFile } from 'antd';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export const useFunctionariesList = () => {
	const { t } = useTranslation();
	const [functionaryList, setFunctionaryList] = useState<FunctionaryList>({
		functionary: [],
		pagination: { totalNoOfRecords: 0 },
	});
	const [isFilterTextApplied, setIsFilterTextApplied] =
		useState<boolean>(false);
	const { initiated, srcmId } = useAuth();
	const { batchId, navigateWithBatchId, queryParams } = useBatchId();
	const filterStateSnap = useSnapshot(FunctionariesListState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [filter, setFilter] = useState<ApplicationsFilter>(() => {
		if (filterStateSnap.filterBy) {
			return { ...filterStateSnap.filterBy } as ApplicationsFilter;
		} else {
			return {
				status: [ApplicationStatus.FUNCTIONARY_ASSIGNED],
			};
		}
	});
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();

	const functionarySrcmIdParam = useMemo(() => {
		const functionarySIdParam = queryParams.get('functionarySrcmIdParam');
		if (typeof functionarySIdParam !== 'string') {
			return '-1';
		}

		return functionarySIdParam;
	}, [queryParams]);

	const navigateWithFunctionarySrcmId = useCallback(
		(route: string, functionarySIdParam?: string, params?: NavigateOptions) => {
			const proposerSrcmIdToUse = functionarySIdParam
				? `${functionarySIdParam}`
				: functionarySrcmIdParam;
			queryParams.set('functionarySrcmIdParam', proposerSrcmIdToUse);
			navigateWithBatchId(route, params);
		},
		[navigateWithBatchId, functionarySrcmIdParam, queryParams]
	);

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		FunctionariesListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const fetchFunctionariesList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationFilter: ApplicationsFilter
		) => {
			if (initiated && srcmId) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const response = await getFunctionaryList({
						batchId,
						filter: applicationFilter,
						pageIndex,
						pageSize,
						functionarySrcmId: srcmId,
					});
					if (
						response.pagination.totalNoOfPages &&
						response.pagination.totalNoOfPages > 0 &&
						pageNumber > response.pagination.totalNoOfPages
					) {
						onPageChange(response.pagination.totalNoOfPages ?? 1, pageSize);
					} else {
						setFunctionaryList(response);
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				}
				setLoading(false);
			}
		},
		[batchId, initiated, onPageChange, setLoading, showToastMessage, srcmId]
	);

	const onFilterChange = useCallback(
		(filterParam: ApplicationsFilter) => {
			const {
				searchByProposerSrcmId,
				zoneNames,
				searchByFunctionaryName,
				searchByFunctionaryEmail,
				roleFilters,
			} = filterParam;
			if (
				searchByProposerSrcmId ||
				zoneNames ||
				searchByFunctionaryName ||
				searchByFunctionaryEmail ||
				roleFilters
			) {
				setIsFilterTextApplied(true);
			} else {
				setIsFilterTextApplied(false);
			}
			FunctionariesListState.filterBy = { ...filterParam };
			setFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const downloadTemplateForBulkFunctionaries = useCallback(
		async (functionaryType: string) => {
			if (srcmId) {
				setLoading(true);
				try {
					const downloadResponse =
						await downloadTemplateForBulkUploadFunctionaries({
							functionarySrcmId: srcmId,
							functionaryType,
						});
					if (downloadResponse.errorMessage) {
						showToastMessage('error', downloadResponse.errorMessage);
						return;
					}
					if (downloadResponse.csvReportSignedUrl) {
						let fileName = 'Bulkupload_Template';
						fileName += '.xlsx';
						const blobResponse = await getAndDownloadFile(
							downloadResponse.csvReportSignedUrl
						);
						if (blobResponse) {
							downloadFile(blobResponse, fileName);
						}
					}
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				} finally {
					setLoading(false);
				}
			}
		},
		[setLoading, showToastMessage, srcmId]
	);

	const bulkUploadDataFunctionaries = useCallback(
		async (
			signedURLDetail: Array<SignedUrlDetail>,
			file: UploadFile<Blob>,
			fileExtension: string | undefined,
			functionaryType: string
		) => {
			if (!srcmId) {
				return;
			}
			setLoading(true);
			try {
				const signedURLResponse = await getSignedUrl({
					functionarySrcmId: srcmId,
					signedUrlDetail: signedURLDetail,
				});
				if (
					!signedURLResponse.signedUrlDetail.length ||
					signedURLResponse.signedUrlDetail.length === 0
				) {
					showToastMessage('error', 'Unknown error occurred');
					return;
				}
				// Uploading file to Cloud
				await uploadFilesToCloud(
					file,
					fileExtension,
					signedURLResponse.signedUrlDetail[0].signedUrl
				);
				if (!signedURLResponse.signedUrlDetail[0].objectPath) {
					showToastMessage('error', 'Unknown error occurred');
					return;
				}
				const cloudFileName =
					signedURLResponse.signedUrlDetail[0].objectPath.split('.com/')[1];
				const uploadResponse = await bulkUploadFunctionaries({
					functionarySrcmId: srcmId,
					fileNameInCloudStorage: cloudFileName,
					batchId,
					functionaryType,
				});
				if (isEmpty(uploadResponse)) {
					showToastMessage('success', t('uploadedSuccessfully'));
					window.location.reload();
				} else if (uploadResponse.errorMessage) {
					showToastMessage('error', uploadResponse.errorMessage);
				} else {
					let fileName = 'Errors_File';
					fileName += '.xlsx';
					const blobResponse = await getAndDownloadFile(
						uploadResponse.csvReportSignedUrl
					);
					if (blobResponse) {
						downloadFile(blobResponse, fileName);
					}
					showToastMessage('error', t('resultsUploadErrorMessage'));
				}
			} catch (e) {
				if (e instanceof AxiosError) {
					// eslint-disable-next-line
					showToastMessage('error', e?.response?.data?.message || e.message);
				} else if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			} finally {
				setLoading(false);
			}
		},
		[setLoading, showToastMessage, srcmId, batchId, t]
	);

	useEffect(() => {
		void fetchFunctionariesList(
			paging.pageNo ?? 1,
			paging.pageSize ?? DEFAULT_PAGE_SIZE,
			filter
		);
	}, [fetchFunctionariesList, filter, paging]);

	return {
		functionaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		functionarySrcmIdParam,
		navigateWithFunctionarySrcmId,
		isFilterTextApplied,
		setIsFilterTextApplied,
		downloadTemplateForBulkFunctionaries,
		bulkUploadDataFunctionaries,
	};
};
